import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Resets scroll on route change so a switch from "/people" → "/offices"
// doesn't dump the user halfway down the new page. When the target carries a
// hash (e.g. footer "Practice" → /practice#practice-III), scroll that anchor
// into view instead — scroll-padding-top offsets the sticky topbar, and each
// .practice-row is a snap point so section snapping doesn't fight the jump.
export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  useLayoutEffect(() => {
    let cancelled = false;
    let frame;
    let restoreSnap;
    const scroll = (el) => {
      const root = document.documentElement;
      const prevSnap = root.style.scrollSnapType;
      const prevAnchor = root.style.overflowAnchor;
      root.style.scrollSnapType = 'none';
      root.style.overflowAnchor = 'none';
      restoreSnap = () => {
        root.style.scrollSnapType = prevSnap;
        root.style.overflowAnchor = prevAnchor;
      };
      // 'auto' inherits smooth scrolling. Route changes must land before paint.
      if (el) el.scrollIntoView({ block: 'start', behavior: 'instant' });
      else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      frame = requestAnimationFrame(() => {
        // Finish after all route layout effects (including SnapPage cleanup).
        // This is a bounded transition adjustment, never a scrolling loop.
        if (el) el.scrollIntoView({ block: 'start', behavior: 'instant' });
        else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        restoreSnap();
        restoreSnap = null;
      });
    };
    const jump = () => {
      if (cancelled) return;
      const el = document.getElementById(hash.slice(1));
      scroll(el);
    };

    // Wait for web fonts before measuring, so an initial deep-link doesn't scroll
    // against a pre-font layout that then reflows out from under it.
    if (!hash) {
      scroll();
    } else if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) frame = requestAnimationFrame(jump); });
    } else {
      frame = requestAnimationFrame(jump);
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      restoreSnap?.();
    };
  }, [pathname, hash, key]);
  return null;
}
